import { Seo } from "@/components/Seo";
import { ModuleTrialPrompt } from "@/components/ModuleTrialPrompt";
import { useState, useRef, useEffect, useCallback } from "react";
import {
  Languages, Loader2, UploadCloud, Sparkles, X, Paperclip, ArrowRight,
  Copy, Download, RotateCcw, FileText, Check,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Disclaimer } from "@/components/Disclaimer";
import { ChatHistory } from "@/components/ChatHistory";
import { LimitModal } from "@/components/LimitModal";
import { toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

export default function VerseTranslation() {
  const [phase, setPhase] = useState("upload"); // upload | processing | result
  const [mode, setMode] = useState("file"); // file | text
  const [file, setFile] = useState(null);
  const [pasteText, setPasteText] = useState("");
  const [targetLang, setTargetLang] = useState("en");
  const [languages, setLanguages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);
  const [copied, setCopied] = useState(false);
  const [limitModal, setLimitModal] = useState(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const { user } = useAuth();

  const loadLangs = useCallback(() => {
    api.get("/translate/languages").then(({ data }) => setLanguages(data.languages)).catch(() => {});
  }, []);
  useEffect(() => { loadLangs(); }, [loadLangs]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const langName = (code) => languages.find((l) => l.code === code)?.name || code;

  const showResult = (data) => {
    setResult(data);
    setActiveId(data.id);
    setPhase("result");
    setHistoryKey((k) => k + 1);
  };

  const pollStatus = (id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/translate/${id}/status`);
        setProgress(data.progress || null);
        if (data.status === "ready") {
          clearInterval(pollRef.current);
          const full = await api.get(`/translate/${id}`);
          showResult(full.data);
        } else if (data.status === "failed") {
          clearInterval(pollRef.current);
          toast.error(data.error || "We couldn't translate this document.");
          reset();
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const translateFile = async () => {
    if (!file) { toast.error("Upload a document or image to translate."); return; }
    if (file.size > MAX_UPLOAD_BYTES) { toast.error("File too large. The maximum file size is 100 MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("target_language", targetLang);
      fd.append("source_language", "auto");
      const { data } = await api.post("/translate", fd);
      if (data.status === "processing") {
        setActiveId(data.id);
        setProgress({ done: 0, total: data.pages || 0 });
        setPhase("processing");
        setHistoryKey((k) => k + 1);
        pollStatus(data.id);
        return;
      }
      showResult(data);
    } catch (e) {
      if (e.response?.status === 402) { setLimitModal(e.response.data?.detail?.reason === "free_limit" ? "free" : "guest"); return; }
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const translatePasted = async () => {
    if (!pasteText.trim()) { toast.error("Paste some text to translate."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/translate/text", { text: pasteText, target_language: targetLang, source_language: "auto" });
      showResult(data);
    } catch (e) {
      if (e.response?.status === 402) { setLimitModal(e.response.data?.detail?.reason === "free_limit" ? "free" : "guest"); return; }
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setPhase("upload"); setFile(null); setPasteText(""); setResult(null); setProgress(null); setActiveId(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const loadSession = async (id) => {
    try {
      const { data } = await api.get(`/translate/${id}`);
      if (data.status === "processing") {
        setActiveId(id); setProgress(data.progress || null); setPhase("processing"); pollStatus(id); return;
      }
      if (data.status === "failed") { toast.error(data.error || "This translation failed."); return; }
      setTargetLang(data.target_language || "en");
      showResult(data);
    } catch { toast.error("Could not load that translation."); }
  };

  const copyOut = async () => {
    try {
      await navigator.clipboard.writeText(result?.translated_text || "");
      setCopied(true); setTimeout(() => setCopied(false), 1800);
      toast.success("Translation copied");
    } catch {
      toast.error("Clipboard access blocked by your browser");
    }
  };

  const downloadOut = () => {
    const blob = new Blob([result?.translated_text || ""], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(result?.doc_name || "translation").replace(/\.[^.]+$/, "")}_${langName(result?.target_language)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12" data-testid="translate-page">
      <ModuleTrialPrompt />
      <Seo title="Verse Translation — AI Legal Document Translation | JusticeVerse" description="Translate any legal document into English or 23 languages while preserving the original format. Upload an image or PDF in Hindi or any regional language. JusticeVerse Verse Translation." />
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold flex items-center gap-2">
          <Languages className="w-4 h-4" /> Verse Translation
        </span>
        <ChatHistory
          user={user}
          listUrl="/translate/history"
          normalize={(d) => d.map((s) => ({ id: s.id, title: `${s.doc_name} → ${langName(s.target_language)}`, created_at: s.created_at }))}
          onSelect={loadSession}
          onNew={reset}
          deleteUrl={(id) => `/translate/${id}`}
          deleteAllUrl="/translate/history"
          activeId={activeId}
          reloadKey={historyKey}
          label="Translation History"
        />
      </div>

      <h1 className="font-serif text-4xl sm:text-5xl font-light text-ink mt-3">Verse Translation</h1>
      <p className="text-inkmuted mt-4 max-w-2xl">
        Translate <span className="text-ink">any legal document</span> — judgments, contracts, notices, FIRs, agreements — into <span className="gold-text">English or any language</span>. Upload a <span className="text-ink">PDF, DOCX, image or scanned page</span> (even in Hindi or any regional language) and get a clean translation that preserves the original format.
      </p>

      {/* Upload / paste phase */}
      {phase === "upload" && (
        <div className="glass-card rounded-3xl p-7 mt-8 space-y-5" data-testid="translate-input">
          <div className="flex gap-2" data-testid="mode-toggle">
            {[["file", "Upload Document / Image"], ["text", "Paste Text"]].map(([k, label]) => (
              <button key={k} onClick={() => setMode(k)} data-testid={`mode-${k}`}
                className={`flex-1 rounded-full px-4 py-2.5 text-xs font-mono uppercase tracking-[0.12em] border transition-all ${mode === k ? "bg-btn text-btnink border-ink" : "border-hairline text-inkmuted hover:text-ink"}`}>
                {label}
              </button>
            ))}
          </div>

          {mode === "file" ? (
            <div onClick={() => fileRef.current?.click()}
              className="border border-dashed border-hairline rounded-2xl p-10 text-center cursor-pointer hover:border-gold/40 transition-all"
              data-testid="translate-dropzone">
              <UploadCloud className="w-8 h-8 text-gold mx-auto" />
              {file ? (
                <div className="mt-3 flex items-center justify-center gap-2 text-sm text-ink">
                  <Paperclip className="w-4 h-4 text-gold" /> {file.name}
                  <button onClick={(e) => { e.stopPropagation(); setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-inkfaint hover:text-red-400"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <p className="text-sm text-inkmuted mt-3">Upload your document — <span className="text-ink">PDF, DOCX, TXT or image</span><br /><span className="text-inkfaint text-xs">Any language · scanned / photographed pages read automatically · max 100 MB · up to 2000 pages.</span></p>
              )}
              <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,image/*,.png,.jpg,.jpeg,.webp,.heic,.heif,.tiff,.bmp" className="hidden" data-testid="translate-file-input"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  if (f && f.size > MAX_UPLOAD_BYTES) { toast.error("File too large. The maximum file size is 100 MB."); e.target.value = ""; return; }
                  setFile(f);
                }} />
            </div>
          ) : (
            <textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder="Paste the text you want to translate (any language)…"
              data-testid="translate-textarea"
              className="w-full h-44 bg-surface border border-hairline rounded-2xl px-4 py-3.5 text-sm outline-none focus:border-gold/40 text-ink resize-none"
            />
          )}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex items-center gap-2 glass-card rounded-xl px-3 py-1.5" data-testid="target-lang-box">
              <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-inkfaint whitespace-nowrap">Translate into</span>
              <Select value={targetLang} onValueChange={setTargetLang}>
                <SelectTrigger className="w-[180px] bg-transparent border-0 text-sm focus:ring-0 text-ink" data-testid="target-lang-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-panel border-hairline text-ink shadow-xl max-h-72">
                  {languages.map((l) => (
                    <SelectItem key={l.code} value={l.code} data-testid={`target-lang-${l.code}`} className="text-sm focus:bg-gold/15 focus:text-ink data-[state=checked]:text-gold">
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <button
              onClick={mode === "file" ? translateFile : translatePasted}
              disabled={busy || (mode === "file" ? !file : !pasteText.trim())}
              data-testid="translate-btn"
              className="flex-1 rounded-full bg-btn text-btnink hover:bg-btnhover disabled:opacity-50 px-6 py-3 text-sm font-semibold uppercase tracking-wider transition-all flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {busy ? "Translating…" : "Translate Now"}
            </button>
          </div>
        </div>
      )}

      {/* Processing (background OCR + translate of large scans) */}
      {phase === "processing" && (
        <div className="glass-card rounded-3xl p-10 mt-8 text-center" data-testid="translate-processing">
          <Loader2 className="w-10 h-10 text-gold mx-auto animate-spin" />
          <h3 className="font-serif text-2xl text-ink mt-6">Reading & translating your document…</h3>
          <p className="text-sm text-inkmuted mt-3 max-w-md mx-auto">
            This looks like a scanned / image document, so I am reading every page and translating it into {langName(targetLang)}. Large files can take a few minutes — the result opens automatically when ready.
          </p>
          {progress?.total > 0 && (
            <div className="mt-7 max-w-sm mx-auto" data-testid="translate-progress">
              <div className="h-2 rounded-full bg-surface2 overflow-hidden">
                <div className="h-full bg-gold transition-all duration-500"
                  style={{ width: `${Math.min(100, Math.round((progress.done / progress.total) * 100))}%` }} />
              </div>
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-inkfaint mt-3">
                {progress.done} / {progress.total} pages
              </p>
            </div>
          )}
          <button onClick={reset} data-testid="cancel-translate-btn"
            className="mt-8 text-xs font-mono uppercase tracking-wider text-inkfaint hover:text-ink transition-colors">
            Translate a different document
          </button>
        </div>
      )}

      {/* Result phase */}
      {phase === "result" && result && (
        <div className="mt-8 space-y-4" data-testid="translate-result">
          <div className="flex flex-wrap items-center justify-between gap-3 glass-card rounded-2xl px-5 py-3">
            <span className="flex items-center gap-2 text-sm text-inkmuted min-w-0">
              <FileText className="w-4 h-4 text-gold shrink-0" />
              <span className="truncate">{result.doc_name}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-inkfaint shrink-0">
                · {result.source_language || "Auto"} <ArrowRight className="w-3 h-3 inline" /> {langName(result.target_language)}
              </span>
            </span>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={copyOut} data-testid="copy-translation-btn" className="rounded-full border border-hairline text-ink hover:bg-surface2 px-4 py-1.5 text-xs font-mono uppercase tracking-wider flex items-center gap-1.5">
                {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />} Copy
              </button>
              <button onClick={downloadOut} data-testid="download-translation-btn" className="rounded-full border border-hairline text-ink hover:bg-surface2 px-4 py-1.5 text-xs font-mono uppercase tracking-wider flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> Download
              </button>
              <button onClick={reset} data-testid="new-translation-btn" className="rounded-full border border-hairline text-inkmuted hover:text-ink px-4 py-1.5 text-xs font-mono uppercase tracking-wider flex items-center gap-1.5">
                <RotateCcw className="w-3.5 h-3.5" /> New
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="glass-card rounded-2xl p-5" data-testid="original-panel">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkfaint mb-3">Original · {result.source_language || "Auto"}</p>
              <pre className="whitespace-pre-wrap font-sans text-sm text-inkmuted leading-relaxed max-h-[60vh] overflow-y-auto">{result.original_text}</pre>
            </div>
            <div className="glass-card rounded-2xl p-5 border border-gold/20" data-testid="translated-panel">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold mb-3">Translation · {langName(result.target_language)}</p>
              <pre className="whitespace-pre-wrap font-sans text-sm text-ink leading-relaxed max-h-[60vh] overflow-y-auto">{result.translated_text}</pre>
            </div>
          </div>
        </div>
      )}

      <Disclaimer className="mt-10" />
      <LimitModal mode={limitModal} onClose={() => setLimitModal(null)} />
    </div>
  );
}
